import React from 'react';
import { Boxes, ShieldCheck, Layers, Award, Info } from 'lucide-react';
import { aaiApi } from '../../../services/aaiApi';
import type { ImpactData, ImpactRoi } from '../../../types/aai';
import { useAsync } from '../useAsync';
import { Loading, ErrorNote } from '../components';
import { Card, MonoLabel, PageHeader } from '../dashboards/ui';

// Module 6 — leadership impact view. Leads with the ROI headline (maturity +
// measured adoption velocity + hours redirected as a range) the design calls for,
// then the org's AI-usable IP. Aggregates only; personal skills are counts, never
// scores. ROI numbers are real where instrumented and honestly marked where not.
const Stat: React.FC<{ label: string; value: React.ReactNode; hint?: string; valueClass?: string }> = ({ label, value, hint, valueClass = 'text-gray-900' }) => (
  <div className="bg-card rounded-xl px-4 py-3">
    <div className={`text-[22px] font-bold tracking-tight leading-tight ${valueClass}`}>{value}</div>
    <div className="text-[11.5px] text-ink-soft">{label}</div>
    {hint && <div className="text-[10.5px] text-gray-400 mt-0.5">{hint}</div>}
  </div>
);

const cap = (s: string | null) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '—');

// The ROI headline block — the design's four stats, 8-week velocity bars, and
// leading-indicator signals. Real data drives maturity / velocity / hours; the
// signals not yet instrumented are labelled as such rather than faked.
const RoiHeadline: React.FC<{ roi: ImpactRoi }> = ({ roi }) => {
  const tr = roi.time_redirected;
  const hours = roi.suppressed ? '—' : `${tr.low.toLocaleString()}–${tr.high.toLocaleString()}`;
  const pts = roi.velocity.points;
  const maxPct = Math.max(1, ...pts.map((p) => p.maturity_pct));
  const signals = [
    { k: 'Adoption sentiment', v: 'Coming soon', note: 'A leading indicator — warms before ROI moves. Not yet instrumented.', bg: 'bg-gray-100', fg: 'text-gray-500' },
    { k: 'Data readiness', v: 'Coming soon', note: 'Poor data quality is the #1 adoption barrier. Instrumentation is on the roadmap.', bg: 'bg-gray-100', fg: 'text-gray-500' },
    { k: 'Peer benchmark', v: roi.maturity_band ? cap(roi.maturity_band) : 'Coming soon', note: 'Your current band. Cross-company benchmarking is on the roadmap.', bg: 'bg-brand-50', fg: 'text-brand-700' },
  ];
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-4">
        <Stat label="Hrs/mo redirected · net of rework" value={hours} hint={roi.suppressed ? undefined : `${tr.unit} · ${roi.onboarded} onboarded`} />
        <Stat label="Company maturity" value={roi.maturity_pct != null ? `${roi.maturity_pct}%` : '—'} />
        <Stat label="Since first measure" value={roi.velocity.delta_pts != null ? `${roi.velocity.delta_pts >= 0 ? '+' : ''}${roi.velocity.delta_pts} pts` : '—'} valueClass={roi.velocity.delta_pts != null && roi.velocity.delta_pts >= 0 ? 'text-emerald-600' : 'text-gray-900'} />
        <Stat label="Current band" value={cap(roi.maturity_band)} hint="peer benchmarking coming soon" />
      </div>

      <Card className="mb-4">
        <div className="font-bold text-[14px]">Adoption velocity · {pts.length || 8} weeks</div>
        <p className="text-[11.5px] text-gray-500 mt-0.5 mb-4">Measured maturity per week — real re-measurements, suppressed under the min group size.</p>
        {roi.velocity.suppressed || pts.length === 0 ? (
          <p className="text-[12.5px] text-gray-400 py-6 text-center">Not enough measured weeks yet — the trend appears as your team re-checks readiness.</p>
        ) : (
          <div className="flex items-end gap-2.5 h-[150px]">
            {pts.map((p, i) => (
              <div key={p.week} className="flex-1 flex flex-col items-center justify-end h-full gap-1.5" title={`${p.week}: ${p.maturity_pct}% · ${p.measured} measured`}>
                <div className="w-full rounded-t-md bg-brand-500" style={{ height: `${Math.max(4, Math.round((p.maturity_pct / maxPct) * 100))}%` }} />
                <span className="font-sans text-[10px] text-gray-400">W{i + 1}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 mb-4">
        {signals.map((sig) => (
          <div key={sig.k} className="bg-card rounded-aa p-[18px]">
            <div className="font-sans text-[10.5px] uppercase tracking-wider text-ink-soft font-semibold mb-2">{sig.k}</div>
            <span className={`inline-block text-[13px] font-bold rounded-full px-3 py-0.5 ${sig.bg} ${sig.fg}`}>{sig.v}</span>
            <p className="text-[12px] text-gray-500 mt-2.5 leading-relaxed m-0">{sig.note}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2.5 bg-brand-50 rounded-xl px-4 py-3 mb-6">
        <Info size={16} className="text-brand-500 shrink-0" />
        <span className="text-[12.5px] text-gray-600">No number is shown as more precise than its source. As telemetry matures, scoring shifts to hard, telemetry-backed ROI.</span>
      </div>
    </>
  );
};

const ImpactDashboardPage: React.FC = () => {
  const { data, loading, error } = useAsync<ImpactData>(() => aaiApi.getImpact(), 'Could not load the impact view.');
  if (loading) return <Loading label="Loading impact…" />;
  if (error || !data) return <ErrorNote message={error || 'Something went wrong.'} />;

  const s = data.skills;
  const packs = data.dept_packs;
  const pct = (v: number | null) => (v != null ? `${Math.round(v * 100)}%` : '—');

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        title="AI impact"
        subtitle="Maturity, adoption velocity, and hours redirected — then the IP your people have proven, shared, and adopted."
      />

      {data.roi && <RoiHeadline roi={data.roi} />}

      <MonoLabel className="mb-3">Your AI-usable IP</MonoLabel>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-4">
        <Stat label="Personal skills" value={s.ip_counts.personal} hint="private to their authors" />
        <Stat label="Team skills" value={s.ip_counts.team} />
        <Stat label="Company skills" value={s.ip_counts.company} />
        <Stat label="Vetted" value={s.ip_counts.vetted} hint="run, revised & proven" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <MonoLabel className="mb-3">Shared-skill performance</MonoLabel>
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-[26px] font-semibold text-gray-900">{pct(s.shared_performance.avg_success_rate)}</span>
            <span className="text-[12px] text-gray-500">worked, across {s.shared_performance.total_runs} runs</span>
          </div>
          <p className="text-[12px] text-gray-500">
            <ShieldCheck size={12} className="inline text-emerald-500" /> {s.shared_performance.adoption} people using shared skills.
          </p>
          <p className="text-[11px] text-gray-400 mt-2">Personal skills are counted but never scored — that stays private.</p>
        </Card>

        <Card>
          <MonoLabel className="mb-3">Sharing funnel</MonoLabel>
          <div className="space-y-1.5 text-[13px]">
            <div className="flex justify-between"><span className="text-gray-600">Proposed</span><span className="font-semibold">{s.graduations.proposed}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Approved</span><span className="font-semibold text-emerald-600">{s.graduations.approved}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Declined</span><span className="font-semibold text-gray-400">{s.graduations.declined}</span></div>
          </div>
        </Card>
      </div>

      <Card className="mt-4">
        <div className="flex items-center gap-2 mb-3"><Layers size={15} className="text-brand-500" /><MonoLabel>Department packs</MonoLabel></div>
        {packs.length === 0 ? (
          <p className="text-[13px] text-gray-400">No team skills graduated yet — they appear here as people share what works.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {packs.map((p) => (
              <div key={p.department} className="bg-white rounded-lg border border-gray-100 px-3.5 py-3">
                <div className="flex items-center gap-2 mb-1">
                  <Boxes size={14} className="text-brand-500" />
                  <span className="text-[13px] font-semibold text-gray-900">{p.department} plugin</span>
                  <span className="text-[11px] text-gray-400 ml-auto">{p.count} skill{p.count === 1 ? '' : 's'} · {p.adoption} using</span>
                </div>
                <div className="text-[12px] text-gray-500 truncate">{p.skills.join(' · ')}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {s.top_skills.length > 0 && (
        <Card className="mt-4">
          <div className="flex items-center gap-2 mb-3"><Award size={15} className="text-amber-500" /><MonoLabel>Most adopted skills</MonoLabel></div>
          <div className="space-y-1.5">
            {s.top_skills.map((t, i) => (
              <div key={i} className="flex items-center gap-2 text-[13px] bg-gray-50 rounded-lg px-3 py-2">
                <span className="font-medium text-gray-800 min-w-0 truncate flex-1">{t.name}</span>
                {t.department && <span className="text-[11px] text-gray-400">{t.department}</span>}
                {t.success_rate != null && <span className="text-[11px] text-emerald-600 font-semibold">{pct(t.success_rate)} worked</span>}
                <span className="text-[11px] text-gray-500">{t.adoption} using</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default ImpactDashboardPage;
